import { useState } from 'react';
import type { Template, Software, SharedDisk } from '../types';
import { mockSoftware } from '../mockData';

interface TemplateFormModalProps {
  template?: Template;
  onClose: () => void;
  onSave: (template: Template) => void;
}

export default function TemplateFormModal({ template, onClose, onSave }: TemplateFormModalProps) {
  const [formData, setFormData] = useState<Template>(template || {
    id: `TPL-${Date.now()}`,
    name: '',
    purpose: '',
    os: 'Windows 11 Pro',
    preInstalledSoftware: [],
    currentVersion: 'v1.0.0',
    status: 'draft',
    baseImage: '',
    minConfig: {
      cpuCores: 4,
      memory: 8,
      systemDisk: 128,
      requirePhysical: false,
    },
    sharedDisks: [],
    snapshots: [],
  });

  const [showSoftwareModal, setShowSoftwareModal] = useState(false);
  const [showDiskModal, setShowDiskModal] = useState(false);
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [softwareCategory, setSoftwareCategory] = useState('all');
  const [newDisk, setNewDisk] = useState<SharedDisk>({
    id: '',
    name: '',
    mountPoint: 'D:',
    space: 500,
    owner: '平台',
  });
  const [gpuModels, setGpuModels] = useState<string[]>(formData.minConfig.gpu?.models || []);
  const [newGpuModel, setNewGpuModel] = useState('');

  const categories = Array.from(new Set(mockSoftware.map(s => s.category)));

  const filteredSoftware = mockSoftware.filter(sw => {
    if (softwareSearch && !sw.name.toLowerCase().includes(softwareSearch.toLowerCase())) return false;
    if (softwareCategory !== 'all' && sw.category !== softwareCategory) return false;
    return !formData.preInstalledSoftware.some(s => s.id === sw.id);
  });

  const handleAddSoftware = (software: Software) => {
    setFormData({
      ...formData,
      preInstalledSoftware: [...formData.preInstalledSoftware, software],
    });
  };

  const handleRemoveSoftware = (id: string) => {
    setFormData({
      ...formData,
      preInstalledSoftware: formData.preInstalledSoftware.filter(s => s.id !== id),
    });
  };

  const handleAddDisk = () => {
    if (!newDisk.name || !newDisk.mountPoint) {
      alert('请填写云盘名称和挂载点');
      return;
    }
    setFormData({
      ...formData,
      sharedDisks: [...formData.sharedDisks, { ...newDisk, id: `disk-${Date.now()}` }],
    });
    setNewDisk({ id: '', name: '', mountPoint: 'D:', space: 500, owner: '平台' });
    setShowDiskModal(false);
  };

  const handleRemoveDisk = (id: string) => {
    setFormData({
      ...formData,
      sharedDisks: formData.sharedDisks.filter(d => d.id !== id),
    });
  };

  const handleAddGpuModel = () => {
    if (newGpuModel && !gpuModels.includes(newGpuModel)) {
      setGpuModels([...gpuModels, newGpuModel]);
      setNewGpuModel('');
    }
  };

  const handleRemoveGpuModel = (model: string) => {
    setGpuModels(gpuModels.filter(m => m !== model));
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.purpose || !formData.baseImage) {
      alert('请填写必填项');
      return;
    }

    const updatedFormData = {
      ...formData,
      minConfig: {
        ...formData.minConfig,
        gpu: gpuModels.length > 0 ? {
          models: gpuModels,
          count: formData.minConfig.gpu?.count || 1,
        } : undefined,
      },
    };

    onSave(updatedFormData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{template ? '编辑模板' : '新建模板'}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入模板名称"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">状态</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Template['status'] })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="draft">草稿</option>
                  <option value="published">已上架</option>
                  <option value="unpublished">已下架</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">当前版本</label>
                <input
                  type="text"
                  value={formData.currentVersion}
                  onChange={(e) => setFormData({ ...formData, currentVersion: e.target.value })}
                  placeholder="如：v1.0.0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">上一版本</label>
                <input
                  type="text"
                  value={formData.previousVersion || ''}
                  onChange={(e) => setFormData({ ...formData, previousVersion: e.target.value })}
                  placeholder="如：v0.9.0"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">操作系统 <span className="text-red-500">*</span></label>
                <select
                  value={formData.os}
                  onChange={(e) => setFormData({ ...formData, os: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="Windows 11 Pro">Windows 11 Pro</option>
                  <option value="Windows 11 Home">Windows 11 Home</option>
                  <option value="Windows 10 Pro">Windows 10 Pro</option>
                  <option value="Ubuntu 22.04 LTS">Ubuntu 22.04 LTS</option>
                  <option value="Ubuntu 20.04 LTS">Ubuntu 20.04 LTS</option>
                  <option value="CentOS 8">CentOS 8</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">模板用途 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.purpose}
                  onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  placeholder="如：软件开发、图形设计等"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">基础镜像 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.baseImage}
                  onChange={(e) => setFormData({ ...formData, baseImage: e.target.value })}
                  placeholder="请输入基础镜像名称"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">预装软件列表</label>
                <button
                  onClick={() => setShowSoftwareModal(true)}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1"></i>
                  新增软件
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">软件名称</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">软件版本</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">保存位置</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">软件描述</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.preInstalledSoftware.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                          暂无预装软件，请点击"新增软件"添加
                        </td>
                      </tr>
                    ) : (
                      formData.preInstalledSoftware.map((sw) => (
                        <tr key={sw.id} className="border-t border-gray-100">
                          <td className="py-2 px-3 text-sm text-gray-900">{sw.name}</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{sw.version}</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{sw.savePath}</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{sw.description}</td>
                          <td className="py-2 px-3">
                            <button
                              onClick={() => handleRemoveSoftware(sw.id)}
                              className="text-red-600 hover:text-red-700 text-xs whitespace-nowrap"
                            >
                              删除
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">最低配置</h4>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-2">CPU核心数</label>
                  <input
                    type="number"
                    value={formData.minConfig.cpuCores}
                    onChange={(e) => setFormData({
                      ...formData,
                      minConfig: { ...formData.minConfig, cpuCores: parseInt(e.target.value) || 0 }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">内存容量 (GB)</label>
                  <input
                    type="number"
                    value={formData.minConfig.memory}
                    onChange={(e) => setFormData({
                      ...formData,
                      minConfig: { ...formData.minConfig, memory: parseInt(e.target.value) || 0 }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">系统盘容量 (GB)</label>
                  <input
                    type="number"
                    value={formData.minConfig.systemDisk}
                    onChange={(e) => setFormData({
                      ...formData,
                      minConfig: { ...formData.minConfig, systemDisk: parseInt(e.target.value) || 0 }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">必须物理机</label>
                  <select
                    value={formData.minConfig.requirePhysical ? 'yes' : 'no'}
                    onChange={(e) => setFormData({
                      ...formData,
                      minConfig: { ...formData.minConfig, requirePhysical: e.target.value === 'yes' }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="no">否</option>
                    <option value="yes">是</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">GPU卡数量</label>
                  <input
                    type="number"
                    value={formData.minConfig.gpu?.count || 0}
                    onChange={(e) => setFormData({
                      ...formData,
                      minConfig: {
                        ...formData.minConfig,
                        gpu: {
                          models: gpuModels,
                          count: parseInt(e.target.value) || 0
                        }
                      }
                    })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-2">GPU可选型号</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newGpuModel}
                      onChange={(e) => setNewGpuModel(e.target.value)}
                      placeholder="输入GPU型号"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <button
                      onClick={handleAddGpuModel}
                      className="px-3 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                    >
                      添加
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {gpuModels.map((model) => (
                      <span key={model} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                        {model}
                        <button onClick={() => handleRemoveGpuModel(model)} className="text-gray-500 hover:text-gray-700">
                          <i className="ri-close-line"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">挂载云盘列表</label>
                <button
                  onClick={() => setShowDiskModal(true)}
                  className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line mr-1"></i>
                  新增云盘
                </button>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">云盘编号</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">云盘别名</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">挂载点</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">云盘空间</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">云盘归属</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.sharedDisks.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-sm text-gray-400">
                          暂无挂载云盘，请点击"新增云盘"添加
                        </td>
                      </tr>
                    ) : (
                      formData.sharedDisks.map((disk) => (
                        <tr key={disk.id} className="border-t border-gray-100">
                          <td className="py-2 px-3 text-sm text-gray-900">{disk.id}</td>
                          <td className="py-2 px-3 text-sm text-gray-900">{disk.name}</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{disk.mountPoint}</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{disk.space} GB</td>
                          <td className="py-2 px-3 text-sm text-gray-600">{disk.owner}</td>
                          <td className="py-2 px-3">
                            <button
                              onClick={() => handleRemoveDisk(disk.id)}
                              className="text-red-600 hover:text-red-700 text-xs whitespace-nowrap"
                            >
                              删除
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap">
            取消
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
            保存
          </button>
        </div>
      </div>

      {showSoftwareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">选择软件</h3>
              <button onClick={() => setShowSoftwareModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-4 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  value={softwareSearch}
                  onChange={(e) => setSoftwareSearch(e.target.value)}
                  placeholder="搜索软件名称"
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <select
                  value={softwareCategory}
                  onChange={(e) => setSoftwareCategory(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">全部分类</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {filteredSoftware.map((sw) => (
                  <div key={sw.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{sw.name}</div>
                      <div className="text-xs text-gray-500">{sw.version} · {sw.category}</div>
                    </div>
                    <button
                      onClick={() => {
                        handleAddSoftware(sw);
                        setShowSoftwareModal(false);
                      }}
                      className="px-3 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 transition-colors whitespace-nowrap"
                    >
                      添加
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showDiskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">新增云盘</h3>
              <button onClick={() => setShowDiskModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">云盘名称</label>
                <input
                  type="text"
                  value={newDisk.name}
                  onChange={(e) => setNewDisk({ ...newDisk, name: e.target.value })}
                  placeholder="请输入云盘名称"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">挂载点</label>
                <input
                  type="text"
                  value={newDisk.mountPoint}
                  onChange={(e) => setNewDisk({ ...newDisk, mountPoint: e.target.value })}
                  placeholder="如：D: 或 /data"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">云盘空间 (GB)</label>
                <input
                  type="number"
                  value={newDisk.space}
                  onChange={(e) => setNewDisk({ ...newDisk, space: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">云盘归属</label>
                <input
                  type="text"
                  value={newDisk.owner}
                  onChange={(e) => setNewDisk({ ...newDisk, owner: e.target.value })}
                  placeholder="如：平台、XX用户"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button onClick={() => setShowDiskModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap">
                取消
              </button>
              <button onClick={handleAddDisk} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap">
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
